/**
 * Token认证工具类
 * 参考后端Sa-Token认证机制实现
 */

import { 
  MainBodyRole, 
  getMainBodyRoleLabel 
} from '@/enums/mainBodyRole'
import { isPersonalUser, isEnterpriseUser } from '@/enums/userType'
import { EnterpriseType } from '@/enums/enterpriseType'
import { UserMainBodyType } from '@/enums/userType'
import { REAL_NAME_AUTH_STATUS } from '@/enums/RealNameAuthStatusEnum'

const TOKEN_KEY = 'PORTAL_TOKEN'
const USER_KEY = 'PORTAL_MAIN_BODY_INFO'
const CAN_SWITCH_IDENTITY_KEY = 'PORTAL_CAN_SWITCH_IDENTITY'

/**
 * 获取Token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * 设置Token
 */
export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * 移除Token
 */
export function removeToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem(CAN_SWITCH_IDENTITY_KEY)
}

/**
 * 当前会话是否允许切换身份（手机号/扫码登录为 true，账号密码登录为 false）
 */
export function getCanSwitchIdentity() {
  return localStorage.getItem(CAN_SWITCH_IDENTITY_KEY) === 'true'
}

/**
 * 设置是否允许切换身份
 * @param {boolean} value
 */
export function setCanSwitchIdentity(value) {
  if (value) {
    localStorage.setItem(CAN_SWITCH_IDENTITY_KEY, 'true')
  } else {
    localStorage.removeItem(CAN_SWITCH_IDENTITY_KEY)
  }
}

/**
 * 获取用户信息
 */
export function getMainBody() {
  const userStr = localStorage.getItem(USER_KEY)
  return userStr ? JSON.parse(userStr) : null
}

/**
 * 设置用户信息
 */
export function setMainBody(mainBody) {
  localStorage.setItem(USER_KEY, JSON.stringify(mainBody))
}

/**
 * 更新当前缓存中的主体头像
 * @param {string} avatarUrl - 新的头像URL
 */
export function updateMainBodyAvatar(avatarUrl) {
  const user = getMainBody()
  if (user) {
    user.mainBodyAvatar = avatarUrl
    setMainBody(user)
  }
}

/**
 * 检查是否已登录
 */
export function isLoggedIn() {
  return !!getToken()
}

/**
 * 当前登录主体是否已完成实名/身份认证
 * @returns {boolean}
 */
export function isIdentityVerified() {
  const user = getMainBody()
  if (!user) return false
  return String(user.mainBodyIsVerified) === REAL_NAME_AUTH_STATUS.CERTIFIED
}

/**
 * 清除所有认证信息
 */
export function clearAuth() {
  removeToken()
}

/**
 * 刷新当前登录主体信息
 */
export async function refreshMainBody() {
  try {
    const { getCurrentUserInfo } = await import('@/api/login')
    const res = await getCurrentUserInfo()
    if (res && res.code === 200) {
      setMainBody(res.data)
      return res.data
    }
  } catch (error) {
    console.error('刷新主体信息失败:', error)
  }
  return null
}

/**
 * ==================== 全局鉴权工具方法 ====================
 * 基于 PORTAL_MAIN_BODY_INFO 缓存中的 mainBodyRole 和 mainBodyType 进行鉴权
 */

export function isEnterpriseMainBody() {
  const user = getMainBody()
  return user?.mainBodyType === UserMainBodyType.ENTERPRISE.value
}

/**
 * 获取当前用户的主体角色 (mainBodyRole)
 * @returns {string|null} 角色值，未登录或不存在时返回 null
 */
export function getMainBodyRole() {
  const user = getMainBody()
  return user?.mainBodyRole || null
}

/**
 * 获取当前用户的主体ID (mainBodyId)
 * @returns {string|null} 主体ID，未登录或不存在时返回 null
 */
export function getCurrentMainBodyId() {
  const user = getMainBody()
  return user?.mainBodyId || null
}


/**
 * 获取当前用户的主体类型 (mainBodyType)
 * @returns {string|null} 类型值 (person/enterprise)，未登录或不存在时返回 null
 */
export function getMainBodyType() {
  const user = getMainBody()
  return user?.mainBodyType || null
}


/**
 * 检查当前用户是否为个人类型
 * @returns {boolean}
 */
export function hasPersonalType() {
  const mainBodyType = getMainBodyType()
  return isPersonalUser(mainBodyType)
}

/**
 * 检查当前用户是否为企业类型
 * @returns {boolean}
 */
export function hasEnterpriseType() {
  const mainBodyType = getMainBodyType()
  return isEnterpriseUser(mainBodyType)
}

/**
 * 检查当前用户是否为企业账号
 * @returns {boolean}
 */
export function hasEnterpriseAccount() {
  const user = getMainBody()
  if(!user) return false;
  if(!user.adminUserId) return false;
  return true;
}

/**
 * 获取当前登录主体角色名称
 * @returns {string|null} 角色名称，未登录或不存在时返回 null
 */
export function getMainBodyRoleName() {
  const mainBodyRole = getMainBodyRole()
  if (!mainBodyRole) {
    return null
  }
  const mainBody = getMainBody()
  let label = getMainBodyRoleLabel(mainBodyRole)
  
  // 不同角色设置不同的颜色背景
  let gradient = ''
  if(mainBodyRole === MainBodyRole.PERSONAL_ROLE.value){
    return null
  } else if (mainBodyRole === MainBodyRole.SUPER_ADMIN_ROLE.value) {
    gradient = 'linear-gradient( 90deg, #9E7017 0%, #CAA661 100%)'
  } else if (mainBodyRole === MainBodyRole.ADMIN_ROLE.value) {
    gradient = 'linear-gradient( 270deg, #C0C0C0 0%, #5A5A5A 100%)'
  } else if (mainBodyRole === MainBodyRole.SALESMAN_ROLE.value) {
    gradient = 'linear-gradient( 270deg, #F3790D 0%, #943C24 100%)'
  }

  if (gradient) {
    label = `<span class="role-name-label" style="background: ${gradient};">${label}</span>`
  }

  if(mainBodyRole === MainBodyRole.SALESMAN_ROLE.value && mainBody.salesmanDisable){
    return label +"<span style='color: red;'>[停用]</span>"
  }
  return label
}


/**
 * 检查当前用户是否为机构业务员角色
 * @returns {boolean}
 */
export function isSalesman() {
  return getMainBodyRole() === MainBodyRole.SALESMAN_ROLE.value
}


/**
 * 检查当前用户是否为机构业务员角色且未被禁用
 * @returns {boolean}
 */
export function isSalesmanNotDisabled() {
  const mainBodyInfo = getMainBody()
  if(!mainBodyInfo) return false;
  return isSalesman() && !mainBodyInfo.salesmanDisable
}

export function canAccessWorkbench() {
  const mainBodyInfo = getMainBody();
  if(!mainBodyInfo) return false;
  return isSalesman() || 
  mainBodyInfo.mainBodyEnterpriseType === EnterpriseType.ORGANIZATION.value  || 
  mainBodyInfo.mainBodyEnterpriseType === EnterpriseType.CHANNEL.value
}

export function hasDataStatisticsPermission() {
  const mainBodyInfo = getMainBody();
  if(!mainBodyInfo) return false;
  // 如果为机构，则需要判断是否为超级管理员或管理员
  return mainBodyInfo.mainBodyEnterpriseType === EnterpriseType.ORGANIZATION.value ||
  mainBodyInfo.mainBodyEnterpriseType === EnterpriseType.CHANNEL.value ||
   mainBodyInfo.mainBodyRole === MainBodyRole.SALESMAN_ROLE.value
}

export function hasSalesmanManagementPermission() {
  const mainBodyInfo = getMainBody();
  if(!mainBodyInfo) return false;
  return mainBodyInfo.mainBodyEnterpriseType === EnterpriseType.ORGANIZATION.value  && (mainBodyInfo.mainBodyRole === MainBodyRole.SUPER_ADMIN_ROLE.value ||
  mainBodyInfo.mainBodyRole === MainBodyRole.ADMIN_ROLE.value)
}


// 检测是否为渠道
export function isChannel() {
  const mainBody = getMainBody();
  return mainBody?.mainBodyEnterpriseType === EnterpriseType.CHANNEL.value
}

// 检测是否为机构
export function isOrganization() {
  const mainBody = getMainBody();
  return mainBody?.mainBodyEnterpriseType === EnterpriseType.ORGANIZATION.value
}

/**
 * 检查当前用户是否为超级管理员
 * @returns {boolean}
 */
export function isSuperAdmin() {
  return getMainBodyRole() === MainBodyRole.SUPER_ADMIN_ROLE.value
}

export function getCurrentMainBodyName() {
  const mainBody = getMainBody()
  if (!mainBody) return ''
  return mainBody.mainBodyName
}

/**
 * 检查当前用户是否为协会会员单位
 * @returns {boolean}
 */
export function isAssociationMember() {
  const mainBody = getMainBody()
  return !!mainBody?.haveMemberUnitTypeId
}

/**
 * ==================== 鉴权对象（全局使用） ====================
 * 将所有鉴权方法封装为对象，方便全局挂载使用
 */
export const auth = {
  // Token 相关
  getToken,
  setToken,
  removeToken,
  getCanSwitchIdentity,
  setCanSwitchIdentity,
  
  // 用户信息相关
  getMainBody,
  getCurrentMainBodyId,
  setMainBody,
  updateMainBodyAvatar,
  isLoggedIn,
  isIdentityVerified,
  clearAuth,
  refreshMainBody,
  
  // 主体类型和角色相关
  getMainBodyRole,
  getMainBodyRoleName,
  getMainBodyType,
  hasPersonalType,
  hasEnterpriseType,
  hasEnterpriseAccount,
  isSalesman,
  isSalesmanNotDisabled,
  canAccessWorkbench,
  hasDataStatisticsPermission,
  isChannel,
  isOrganization,
  isEnterpriseMainBody,
  hasSalesmanManagementPermission,
  isSuperAdmin,
  getCurrentMainBodyName,
  isAssociationMember
}
